import {
  type Identifier,
  type Qualified,
  getName as _getName,
  namesEqual,
  identifierToString,
  toHumanReadableString,
} from '@packages/systems/wfdl-Qualified';

import {identity, constantFalse} from '@packages/utilities/fp/utils';

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
export type VariableType = Identifier | Qualified<Identifier>;

export const unqualified: (arg1: Identifier) => VariableType = identity;
export const qualified: (arg1: Qualified<Identifier>) => VariableType =
  identity;

/**
 * Accepts a matcher obj that accounts for qualified and unqualified callbacks
 * and invokes their callbacks accordingly.
 */
export const match =
  <a>(matcher: {
    unqualified: (arg1: Identifier) => a;
    qualified: (arg1: Qualified<Identifier>) => a;
  }): ((arg1: VariableType) => a) =>
  (variable: VariableType): a =>
    typeof variable === 'string'
      ? matcher.unqualified(variable as Identifier)
      : matcher.qualified(variable as Qualified<Identifier>);

/**
 * Retrieves the name of the Variable.
 */
export const getName = (variable: VariableType): string =>
  match({
    unqualified: (v) => identifierToString(v),
    qualified: (v) => identifierToString(_getName(v)),
  })(variable);

export const print: (variable: VariableType) => string = match({
  unqualified: identifierToString,
  qualified: toHumanReadableString,
});

const unqualifiedEquals = (idToMatch: Identifier) =>
  match({
    unqualified: (id) => id === idToMatch,
    qualified: constantFalse,
  });

const qualifiedEquals = (idToMatch: Qualified<Identifier>) =>
  match({
    qualified: (id) => namesEqual(id, idToMatch),
    unqualified: constantFalse,
  });

/**
 * Tests if two variables are equal
 */

export const equals: (arg1: VariableType) => (arg1: VariableType) => boolean =
  match({
    qualified: qualifiedEquals,
    unqualified: unqualifiedEquals,
  });
